<template>
  <div class="settings-page">
    <!-- Header -->
    <div class="settings-header">
      <div class="settings-header-left" @click="handleBack">
        <van-icon name="arrow-left" size="20" color="#1A1A1A" />
      </div>
      <div class="settings-header-title">{{ $t('settings') }}</div>
      <div class="settings-header-right" />
    </div>

    <!-- Settings list -->
    <div class="settings-body">
      <van-cell-group>
        <van-cell :title="$t('view_guide')" icon="bulb-o" is-link @click="viewGuide" />
        <van-cell :title="$t('settings_language')" icon="font-o" is-link :value="currentLanguageName" @click="showLanguageSheet = true" />
        <van-cell :title="$t('feedback')" icon="chat-o" is-link @click="openFeedbackDialog" />
        <van-cell :title="$t('blocked_users')" icon="warning-o" is-link @click="goBlockedUsers" />
      </van-cell-group>

      <!-- Private Token -->
      <van-cell-group class="settings-token-group">
        <div class="token-section">
          <div class="token-section-title">{{ $t('private_token') }}</div>
          <div class="token-section-desc">{{ $t('token_desc') }}</div>
          <div v-if="privateToken" class="token-display">
            <van-field v-model="privateToken" readonly class="token-field" />
          </div>
          <div class="token-actions">
            <van-button v-if="privateToken" size="small" plain type="primary" @click="copyToken" class="action-btn">
              {{ $t('copy') }}
            </van-button>
            <van-button plain type="primary" size="small" class="action-btn" @click="generateToken" :loading="generatingToken">
              {{ privateToken ? $t('regenerate') : $t('generate') }}
            </van-button>
          </div>
        </div>
      </van-cell-group>

      <van-cell-group class="settings-account-group">
        <van-cell :title="$t('logout')" icon="revoke" is-link @click="handleLogout" />
        <van-cell :title="$t('delete_account')" icon="delete-o" is-link class="delete-account-cell" @click="showDeleteAccountConfirm" />
      </van-cell-group>

      <div class="settings-slogan">{{ $t('slogan_ai') }}</div>
    </div>

    <!-- Feedback dialog -->
    <van-dialog v-model="showFeedbackDialog" :title="$t('feedback_title')" show-cancel-button
      :confirm-button-text="$t('feedback_submit')" :cancel-button-text="$t('cancel')"
      :confirm-button-color="'#E8543A'" @confirm="handleFeedbackSubmit"
      @cancel="showFeedbackDialog = false">
      <div class="feedback-dialog-content">
        <van-field v-model="feedbackContent" type="textarea" rows="4" maxlength="1000" show-word-limit
          :placeholder="$t('feedback_hint')" />
        <div v-if="feedbackList.length > 0" class="feedback-history">
          <div class="feedback-history-title">{{ $t('feedback_history') || 'History' }}</div>
          <div v-for="item in feedbackList" :key="item.feedbackId" class="feedback-item">
            <div class="feedback-item-content">{{ item.content }}</div>
            <div v-if="item.reply" class="feedback-reply">
              <span class="feedback-reply-label">{{ $t('feedback_reply_label') }}</span>
              <span class="feedback-reply-text">{{ item.reply }}</span>
            </div>
            <div class="feedback-item-time">{{ formatFeedbackTime(item.createdAt) }}</div>
          </div>
        </div>
        <div v-else class="feedback-empty">{{ $t('feedback_empty') }}</div>
      </div>
    </van-dialog>

    <!-- Delete account warning dialog -->
    <van-dialog v-model="deleteAccountDialog.show" :title="$t('delete_account')" show-cancel-button
      :confirm-button-text="$t('delete')" :confirm-button-color="'#E05252'" @confirm="handleDeleteAccountConfirm"
      @cancel="deleteAccountDialog.show = false">
      <div class="delete-account-content">
        <p class="warning-text">{{ $t('delete_warning') }}</p>
        <p>{{ $t('delete_data_intro') }}</p>
        <ul>
          <li>{{ $t('delete_item_profile') }}</li>
          <li>{{ $t('delete_item_chat') }}</li>
          <li>{{ $t('delete_item_contact') }}</li>
          <li>{{ $t('delete_item_all') }}</li>
        </ul>
      </div>
    </van-dialog>

    <!-- Delete account final confirmation dialog -->
    <van-dialog v-model="deleteAccountFinalDialog.show" :title="$t('final_confirmation')" show-cancel-button
      :confirm-button-text="$t('delete_account')" :confirm-button-color="'#E05252'" @confirm="handleFinalDeleteAccount"
      @cancel="deleteAccountFinalDialog.show = false">
      <div class="delete-account-final-content">
        <p>{{ $t('delete_confirm_prompt') }}</p>
        <van-field v-model="deleteAccountFinalDialog.input" :placeholder="$t('placeholder_type_delete')" clearable />
      </div>
    </van-dialog>
    <!-- Language picker -->
    <van-action-sheet v-model="showLanguageSheet" :actions="languageActions" :cancel-text="$t('cancel')" @select="onLanguageSelect" />
  </div>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import { Cell, CellGroup, Icon, Dialog, Field, Button, Toast, ActionSheet } from 'vant'
import { SUPPORTED_LOCALES } from '../i18n'
import { setLocale, setFollowSystem, getCurrentLanguageName } from '../i18n/helpers'

Vue.use(Cell)
Vue.use(CellGroup)
Vue.use(Icon)
Vue.use(Dialog)
Vue.use(Field)
Vue.use(Button)
Vue.use(Toast)
Vue.use(ActionSheet)

export default {
  name: 'Settings',
  data () {
    return {
      showFeedbackDialog: false,
      feedbackContent: '',
      feedbackList: [],
      privateToken: '',
      generatingToken: false,
      showLanguageSheet: false,
      deleteAccountDialog: {
        show: false
      },
      deleteAccountFinalDialog: {
        show: false,
        input: ''
      }
    }
  },
  mounted () {
    this.loadPrivateToken()
  },
  computed: {
    languageActions () {
      const actions = [{ name: this.$t('settings_language_followSystem') }]
      SUPPORTED_LOCALES.forEach(function (item) {
        actions.push({ name: item.name })
      })
      return actions
    },
    currentLanguageName () {
      return getCurrentLanguageName(this.$t('settings_language_followSystem'))
    }
  },
  methods: {
    loadPrivateToken () {
      try {
        const userId = this.$root.$common.getUserId()
        if (!userId) return

        axios.post('/api/user/get_private_token', { userId: userId }).then((resp) => {
          if (resp.data && resp.data.code == '0' && resp.data.data.token) {
            this.privateToken = resp.data.data.token
          }
        })
      } catch (error) {
        console.error('Failed to load private token:', error)
      }
    },
    async generateToken () {
      try {
        this.generatingToken = true
        const userId = this.$root.$common.getUserId()
        if (!userId) {
          Toast(this.$t('toast_not_logged_in'))
          return
        }

        const resp = await axios.post('/api/user/generate_private_token', { userId: userId })
        if (resp.data && resp.data.code == '0') {
          this.privateToken = resp.data.data.token
          Toast(this.$t('toast_token_generated'))
        } else {
          Toast(this.$t('toast_token_failed'))
        }
      } catch (error) {
        console.error('Failed to generate token:', error)
        Toast(this.$t('toast_token_failed'))
      } finally {
        this.generatingToken = false
      }
    },
    copyToken () {
      if (!this.privateToken) return

      navigator.clipboard.writeText(this.privateToken).then(() => {
        Toast(this.$t('toast_copied'))
      }).catch(() => {
        Toast(this.$t('toast_copy_failed'))
      })
    },
    handleBack () {
      this.$router.back()
    },
    onLanguageSelect (item, index) {
      if (index === 0) {
        setFollowSystem()
      } else {
        const target = SUPPORTED_LOCALES[index - 1]
        if (target) {
          setLocale(target.locale)
        }
      }
      this.showLanguageSheet = false
    },
    viewGuide () {
      this.$store.commit('setShowOnboarding', true)
      this.$router.push({ name: 'FrontPage' })
    },
    goBlockedUsers () {
      this.$router.push('/blocked-users')
    },
    handleLogout () {
      try {
        this.$root.$common.clearTokenHeader()
        this.$store.state.alreadyLogin = false
        Toast(this.$t('toast_logout_success'))
        setTimeout(() => {
          window.location.href = '/app/'
        }, 1000)
      } catch (error) {
        console.error('Logout failed:', error)
        Toast(this.$t('toast_logout_failed'))
      }
    },
    showDeleteAccountConfirm () {
      this.deleteAccountDialog.show = true
    },
    handleDeleteAccountConfirm () {
      this.deleteAccountDialog.show = false
      this.deleteAccountFinalDialog.show = true
      this.deleteAccountFinalDialog.input = ''
    },
    async handleFinalDeleteAccount () {
      const input = this.deleteAccountFinalDialog.input.trim().toLowerCase()
      if (input !== 'delete') {
        Toast(this.$t('delete_confirm_prompt'))
        return
      }

      try {
        const userId = this.$root.$common.getUserId()
        if (!userId) {
          Toast(this.$t('toast_not_logged_in'))
          return
        }

        const resp = await axios.post('/api/user/delete_account', {
          userId: userId
        })

        if (resp.data && resp.data.code === '0') {
          Toast(this.$t('toast_account_deleted'))
          this.$root.$common.clearTokenHeader()
          this.$store.state.alreadyLogin = false
          setTimeout(() => {
            window.location.href = '/app/'
          }, 1500)
        } else {
          Toast(resp.data.msg || this.$t('toast_delete_account_failed'))
        }
      } catch (error) {
        console.error('Delete account error:', error)
        Toast(this.$t('toast_delete_account_failed'))
      } finally {
        this.deleteAccountFinalDialog.show = false
      }
    },
    async openFeedbackDialog () {
      this.feedbackContent = ''
      this.showFeedbackDialog = true
      await this.loadFeedbackList()
    },
    async loadFeedbackList () {
      try {
        const userId = this.$root.$common.getUserId()
        if (!userId) return
        const resp = await axios.post('/api/user/list_feedback', { userId: userId })
        if (resp.data && resp.data.code === '0') {
          this.feedbackList = resp.data.data.feedbacks || []
        }
      } catch (error) {
        console.error('Failed to load feedback list:', error)
      }
    },
    async handleFeedbackSubmit () {
      if (!this.feedbackContent || this.feedbackContent.trim() === '') {
        Toast(this.$t('feedback_hint'))
        return
      }
      try {
        const userId = this.$root.$common.getUserId()
        if (!userId) {
          Toast(this.$t('toast_not_logged_in'))
          return
        }
        const resp = await axios.post('/api/user/create_feedback', {
          userId: userId,
          content: this.feedbackContent.trim()
        })
        if (resp.data && resp.data.code === '0') {
          Toast(this.$t('feedback_submit_success'))
          this.feedbackContent = ''
          await this.loadFeedbackList()
        } else {
          Toast(this.$t('feedback_submit_failed'))
        }
      } catch (error) {
        console.error('Failed to submit feedback:', error)
        Toast(this.$t('feedback_submit_failed'))
      }
    },
    formatFeedbackTime (timeVal) {
      if (!timeVal) return ''
      try {
        var d = new Date(Number(timeVal))
        var mm = String(d.getMonth() + 1).padStart(2, '0')
        var dd = String(d.getDate()).padStart(2, '0')
        var hh = String(d.getHours()).padStart(2, '0')
        var mi = String(d.getMinutes()).padStart(2, '0')
        return mm + '/' + dd + ' ' + hh + ':' + mi
      } catch (e) {
        return timeVal
      }
    }
  }
}
</script>

<style scoped>
.settings-page {
  min-height: 100vh;
  background: #F7F5F2;
}

.settings-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #E0DDD8;
}

.settings-header-left {
  width: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.settings-header-right {
  width: 40px;
}

.settings-header-title {
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #1A1A1A;
}

.settings-body {
  padding: 16px;
}

.settings-token-group {
  margin-top: 16px;
  padding: 16px;
}

.token-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.token-section-title {
  align-self: flex-start;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1A1A1A;
}

.token-section-desc {
  align-self: flex-start;
  font-size: 0.75rem;
  color: #8A8A8A;
  margin-top: 4px;
}

.token-display {
  width: 100%;
  margin-top: 12px;
}

.token-field {
  background: #FAFAF8;
  border-radius: 6px;
  font-family: monospace;
  font-size: 13px;
}

.token-actions {
  display: flex;
  gap: 8px;
  width: 100%;
  margin-top: 12px;
}

.token-actions .action-btn {
  flex: 1;
}

.settings-account-group {
  margin-top: 16px;
}

.settings-slogan {
  margin-top: 32px;
  text-align: center;
  font-size: 13px;
  color: #8A8A8A;
}

.settings-body .van-cell {
  text-align: left;
}

.settings-body .van-cell__title {
  text-align: left;
}
</style>

<style scoped>
/* 注销账号：视觉弱化（vant 内部节点，scoped 需用 >>> 穿透） */
.settings-body .van-cell.delete-account-cell {
  color: #8A8A8A;
}

.settings-body >>> .delete-account-cell .van-cell__title {
  color: #8A8A8A;
}

.settings-body >>> .delete-account-cell .van-cell__left-icon,
.settings-body >>> .delete-account-cell .van-cell__right-icon {
  color: #E0DDD8;
}

.feedback-dialog-content {
  padding: 16px 20px;
  max-height: 60vh;
  overflow-y: auto;
}

.feedback-history {
  margin-top: 16px;
  border-top: 1px solid #E0DDD8;
  padding-top: 12px;
}

.feedback-history-title {
  font-size: 13px;
  color: #8A8A8A;
  margin-bottom: 8px;
}

.feedback-item {
  padding: 8px 0;
  border-bottom: 1px solid #F7F5F2;
}

.feedback-item:last-child {
  border-bottom: none;
}

.feedback-item-content {
  font-size: 14px;
  color: #1A1A1A;
  word-break: break-all;
}

.feedback-item-time {
  font-size: 11px;
  color: #8A8A8A;
  margin-top: 4px;
}

.feedback-reply {
  margin-top: 6px;
  padding: 6px 8px;
  background: #E8F7FB;
  border-radius: 4px;
  border: 0.5px solid #E8F7FB;
}

.feedback-reply-label {
  font-size: 11px;
  color: #2A8FAD;
  font-weight: 600;
  margin-right: 4px;
}

.feedback-reply-text {
  font-size: 13px;
  color: #8A8A8A;
  word-break: break-all;
}

.feedback-empty {
  text-align: center;
  color: #8A8A8A;
  font-size: 13px;
  padding: 16px 0;
}

.delete-account-content {
  padding: 16px 20px;
}

.delete-account-content .warning-text {
  color: #E05252;
  font-weight: bold;
  margin-bottom: 12px;
}

.delete-account-content p {
  margin: 8px 0;
  font-size: 14px;
  color: #1A1A1A;
}

.delete-account-content ul {
  margin: 8px 0;
  padding-left: 20px;
  font-size: 13px;
  color: #8A8A8A;
}

.delete-account-content li {
  margin: 4px 0;
}

.delete-account-final-content {
  padding: 16px 20px;
}

.delete-account-final-content p {
  margin-bottom: 12px;
  font-size: 14px;
  color: #1A1A1A;
}
</style>



// WEBPACK FOOTER //
// src/components/Settings.vue